import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../../../environments/environment';
import { NotificationService } from '../../../../core/services/notification.service';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private token: string | null = null;
  private readonly baseUrl = environment.URL;
  private readonly tokenDurationInMinutes = 2;
  // Mapa dinámico idRuta → ruta (para PermissionGuard)
  // 🔒 NUEVO: Mapa dinámico idRuta → ruta (para PermissionGuard)
  // ============================================================
  private rutaMap: Record<number, string> = {};

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
  ) {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('token');
    }
  }

  // ============================================================
  // 🧠 Autenticación
  // ============================================================

  /** Login normal */
  login(credentials: { username: string; password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}login`, credentials).pipe(
      tap((response: any) => {
        if (response.token) {
          this.setToken(response.token);
        }
      })
    );
  }

  loginWith2FA(username: string, token2FA: string) {
    return this.http.post(`${this.baseUrl}2fa/login`, { username, token: token2FA });
  }

  changeMyPassword(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}editPassword`, data);
  }


  isAuthenticated(): boolean {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');


      if (!token || token.trim() === '' || token === 'undefined' || token.split('.').length !== 3) { return false; }

      try {
        const decoded: any = jwtDecode(token);
        if (!decoded?.exp) return false;
        return decoded.exp * 1000 > Date.now();
      } catch (err) {
        console.error('❌ Error al decodificar token en isAuthenticated:', err);
        localStorage.removeItem('token');
        return false;
      }
    }
    return false;
  }

  getPermissions(roleId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}roles-permisos/${roleId}`);
  }

  getUserRoleId(): number | null {
    if (typeof window !== 'undefined') {
      const token = this.getToken();
      if (!token) return null;
      try {
        const decodedToken: any = jwtDecode(token);
        return decodedToken.rolId ?? decodedToken.idRol ?? null;
      } catch (e) {
        console.error('❌ Error al decodificar token en getUserRoleId:', e);
        return null;
      }
    }
    return null;
  }

  setToken(token: string): void {
    if (typeof window !== 'undefined' && token && token !== 'undefined') {
      this.token = token;
      localStorage.setItem('token', token);
    }
  }


  getToken(): string | null {
    if (typeof window !== 'undefined') {
      // Si no hay token en memoria, intentar leer de localStorage
      if (!this.token) {
        this.token = localStorage.getItem('token');
      }
    }
    return this.token;
  }

  logout(): void {
    this.notificationService.showError('Sesión cerrada por inactividad');

    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('rutaMap');
    }

    this.rutaMap = {};
    this.token = null;
    this.router.navigate(['/login']);
  }

  /** Refresca el token JWT */
  refreshToken(): Observable<string> {
    const token = localStorage.getItem('token');
    return this.http.post<{ token: string }>(`${this.baseUrl}refreshToken`, { token }).pipe(
      tap(response => {
        localStorage.setItem('token', response.token);
        this.token = response.token;
      }),
      map(response => response.token)
    );
  }

  /** Elimina el token */
  removeToken(): void {
    localStorage.removeItem('token');
  }

  /** Decodifica datos del usuario actual */
  getUserData(): any | null {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined') return null;

      try {
        const decoded: any = jwtDecode(token);
        return decoded;
      } catch (err) {
        console.error('❌ Error al decodificar token en getUserData:', err);
        return null;
      }
    }
    return null;
  }

  // ============================================================
  // 🧭 Mapa de rutas dinámicas (idRuta → ruta)
  // ============================================================

  /** Genera el mapa idRuta → ruta desde categorías */
  setRutaMapFromCategorias(categorias: any[]): void {
    const map: Record<number, string> = {};

    categorias.forEach((cat: any) => {
      if (Array.isArray(cat.rutas)) {
        cat.rutas.forEach((r: any) => {
          if (r.id && r.ruta) {
            map[r.id] = r.ruta.toLowerCase();
          }
        });
      }
    });

    this.rutaMap = map;
    localStorage.setItem('rutaMap', JSON.stringify(map));
  }

  /** Obtiene el mapa idRuta → ruta desde memoria o storage */
  getRutaMap(): Record<number, string> {
    if (!this.rutaMap || Object.keys(this.rutaMap).length === 0) {
      const saved = localStorage.getItem('rutaMap');
      if (saved) {
        this.rutaMap = JSON.parse(saved);
      }
    }
    return this.rutaMap || {};
  }

  /** Inicializa el mapa de rutas desde backend (según rol actual) */
  initializeRutaMap(rolId: number): void {
    this.getPermissions(rolId).subscribe({
      next: (permisos: any) => this.setRutaMapFromCategorias(permisos),
      error: (err) => console.error('❌ Error al inicializar rutaMap:', err),
    });
  }

  /** Refresca rutas dinámicamente */
  refrescarRutaMap(): void {
    const user = this.getUserData();
    const rolId = user?.rolId;
    if (rolId) {
      this.initializeRutaMap(rolId);
    } else {
      console.warn('⚠️ No se pudo refrescar rutaMap: sin rolId');
    }
  }

  /** Asigna un mapa manualmente */
  setRutaMap(map: Record<number, string>): void {
    this.rutaMap = map;
    localStorage.setItem('rutaMap', JSON.stringify(map));
  }
}
